// Claims resolver — pure functions over Claim / AuthorityRule / Ruling
// (docs/plans/plant-model-construction.md § 3). No React, no seed imports,
// no mutation: the plant-model surface (construct.R1) and the rule
// lifecycle (construct.R8) both call these; neither redefines them.

import type {
  AuthorityRule,
  Claim,
  ClaimSubject,
  ResolvedVersion,
  Ruling,
  VersionDiff,
} from '../schema/claim';

export function subjectKey(subject: ClaimSubject): string {
  return subject.field ? `${subject.kind}:${subject.id}:${subject.field}` : `${subject.kind}:${subject.id}`;
}

export type DecidedBy = 'observed' | 'ruled' | 'authority' | 'single';

export type Resolution = {
  /** The winning claim per subject key. */
  winners: ReadonlyMap<string, Claim>;
  /** Subjects with more than one live claim and no ruling — the human residue (§ 3.2). */
  disputed: ReadonlyMap<string, readonly Claim[]>;
  /** Why each winner won: which tier decided it. */
  decidedBy: ReadonlyMap<string, DecidedBy>;
};

/** Rank a claim's source for one kind: lower is stronger. Observed beats
 * ruled beats the authority table's document order beats inferred (§ 3.4). */
export function sourceRank(claim: Claim, authority: readonly AuthorityRule[]): number {
  const src = claim.source;
  if (src.tier === 'observed') return 0;
  if (src.tier === 'ruled') return 1;
  if (src.tier === 'case') return 2;
  if (src.tier === 'document') {
    const rule = authority.find((a) => a.kind === claim.kind);
    const i = rule ? rule.ranking.indexOf(src.sourceKind) : -1;
    return 10 + (i === -1 ? rule?.ranking.length ?? 0 : i);
  }
  return 100;
}

/** Resolve every subject: rulings first, then source rank, then the newest
 * vintage. Rejected claims never win; superseded claims never win. Two
 * document claims at the same rank with different values are `disputed`
 * unless a ruling settles them. */
export function resolve(
  claims: readonly Claim[],
  rulings: readonly Ruling[],
  authority: readonly AuthorityRule[],
): Resolution {
  const bySubject = new Map<string, Claim[]>();
  for (const c of claims) {
    if (c.status === 'rejected' || c.status === 'superseded') continue;
    const key = subjectKey(c.subject);
    const list = bySubject.get(key) ?? [];
    list.push(c);
    bySubject.set(key, list);
  }
  const rulingBySubject = new Map<string, Ruling>();
  for (const r of rulings) {
    const key = subjectKey(r.subject);
    const prev = rulingBySubject.get(key);
    if (!prev || r.decidedAt > prev.decidedAt) rulingBySubject.set(key, r);
  }

  const winners = new Map<string, Claim>();
  const disputed = new Map<string, readonly Claim[]>();
  const decidedBy = new Map<string, DecidedBy>();

  for (const [key, list] of bySubject) {
    const ruling = rulingBySubject.get(key);
    const ruled = ruling ? list.find((c) => c.id === ruling.acceptsClaimId) : undefined;
    if (ruled) {
      winners.set(key, ruled);
      decidedBy.set(key, 'ruled');
      continue;
    }
    if (list.length === 1) {
      winners.set(key, list[0]);
      decidedBy.set(key, 'single');
      continue;
    }
    const ranked = [...list].sort((a, b) => {
      const d = sourceRank(a, authority) - sourceRank(b, authority);
      return d !== 0 ? d : b.vintage.localeCompare(a.vintage);
    });
    const top = ranked[0];
    const topRank = sourceRank(top, authority);
    const ties = ranked.filter(
      (c) => sourceRank(c, authority) === topRank && c.valueLabel !== top.valueLabel,
    );
    if (ties.length > 0 && top.source.tier !== 'observed') {
      disputed.set(key, ranked);
      winners.set(key, top);
      decidedBy.set(key, 'authority');
      continue;
    }
    winners.set(key, top);
    decidedBy.set(key, top.source.tier === 'observed' ? 'observed' : 'authority');
  }
  return { winners, disputed, decidedBy };
}

/** Diff two resolved versions over the claim store (§ 4, construct.R3). */
export function diffVersions(
  from: ResolvedVersion,
  to: ResolvedVersion,
  claims: readonly Claim[],
): VersionDiff {
  const byId = new Map(claims.map((c) => [c.id, c] as const));
  const fromBySubject = new Map<string, Claim>();
  const toBySubject = new Map<string, Claim>();
  for (const id of from.claimIds) {
    const c = byId.get(id);
    if (c) fromBySubject.set(subjectKey(c.subject), c);
  }
  for (const id of to.claimIds) {
    const c = byId.get(id);
    if (c) toBySubject.set(subjectKey(c.subject), c);
  }
  const added: Claim[] = [];
  const removed: Claim[] = [];
  const changed: { subject: ClaimSubject; before: Claim; after: Claim }[] = [];
  for (const [key, after] of toBySubject) {
    const before = fromBySubject.get(key);
    if (!before) added.push(after);
    else if (before.id !== after.id) changed.push({ subject: after.subject, before, after });
  }
  for (const [key, before] of fromBySubject) {
    if (!toBySubject.has(key)) removed.push(before);
  }
  return { from: from.id, to: to.id, added, removed, changed };
}

/** The claims a version accepted, in store order. */
export function claimsOfVersion(version: ResolvedVersion, claims: readonly Claim[]): Claim[] {
  const ids = new Set(version.claimIds);
  return claims.filter((c) => ids.has(c.id));
}
